import { useState } from "react";
import { BannerDto } from "../../../Common/api/models/AppConfiguration";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import styles from "./BannerConfigForm.module.scss";
import {
  mapCategoryToSeverity,
  mapVariant,
} from "../../../Common/utils/bannerMapping";
import { CATEGORIES, VARIANTS } from "../model/consts";
import AlertBanner from "../../../Layout/components/AlertBanner/AlertBanner";

interface BannerConfigFormProps {
  initial?: BannerDto | null;
  onSubmit: (banner: Omit<BannerDto, "id"> | BannerDto) => Promise<void>;
  onCancel: () => void;
}

const BannerConfigForm = ({
  initial,
  onSubmit,
  onCancel,
}: BannerConfigFormProps): JSX.Element => {
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    text: initial?.text || "",
    category: initial?.category || "info",
    variant: initial?.variant || "standard",
    customBackgroundColor: initial?.customBackgroundColor || "",
    publishDate: initial?.publishDate || "",
    unpublishDate: initial?.unpublishDate || "",
  });

  // Derived validation state
  const publishDayjs = formData.publishDate
    ? dayjs(formData.publishDate)
    : null;
  const unpublishDayjs = formData.unpublishDate
    ? dayjs(formData.unpublishDate)
    : null;
  const unpublishInvalid = !!(
    publishDayjs &&
    unpublishDayjs &&
    unpublishDayjs.isBefore(publishDayjs)
  );
  const textEmpty = !formData.text.trim();
  const formInvalid = textEmpty || unpublishInvalid;

  const handleSubmit = async () => {
    if (formInvalid) return;

    setSaving(true);

    try {
      const payload: any = {
        id: initial?.id,
        text: formData.text.trim(),
        category: formData.category,
        variant: formData.variant,
        customBackgroundColor: formData.customBackgroundColor || undefined,
        publishDate: formData.publishDate,
        unpublishDate: formData.unpublishDate || undefined,
      };

      await onSubmit(payload);
    } catch (error) {
      console.error("Failed to save banner:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryChange = (e: SelectChangeEvent) => {
    handleInputChange("category", e.target.value.toLowerCase());
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePublishDateChange = (value: Dayjs | null) => {
    handleInputChange("publishDate", value ? value.toISOString() : "");
  };

  const handleUnpublishDateChange = (value: Dayjs | null) => {
    handleInputChange("unpublishDate", value ? value.toISOString() : "");
  };

  return (
    <div className={styles.container}>
      <Paper sx={{ p: 3, maxWidth: "100%", margin: "auto" }}>
        <Typography variant="h5" gutterBottom>
          {initial?.id ? "Edit Banner" : "Create Banner"}
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ mt: 2, width: "100%" }}
        >
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="Text"
              value={formData.text}
              onChange={(e) => handleInputChange("text", e.target.value)}
              multiline
              rows={4}
              helperText={
                textEmpty
                  ? "Banner text is required."
                  : "HTML content that will be displayed on the banner. You can use basic tags (e.g. <strong>, <em>, <br />). For links use: <a href='https://example.com' target='_blank' rel='noopener noreferrer'>Link Text</a>. Include target='_blank' and rel='noopener noreferrer' for security."
              }
              error={textEmpty}
              required
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>Category</InputLabel>
            <Select
              value={formData.category}
              label="Category"
              onChange={handleCategoryChange}
              required
            >
              {CATEGORIES.map((c) => {
                return (
                  <MenuItem
                    key={c.label}
                    value={c.value.toLowerCase()}
                    sx={{ color: c.color }}
                  >
                    <span style={{ color: c.color }}>{c.label}</span>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>Variant</InputLabel>
            <Select
              value={formData.variant}
              label="Variant"
              onChange={(e) => handleInputChange("variant", e.target.value)}
              required={true}
            >
              {VARIANTS.map((v) => (
                <MenuItem key={v} value={v.toLowerCase()}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="Custom Background Color"
              value={formData.customBackgroundColor}
              onChange={(e) =>
                handleInputChange("customBackgroundColor", e.target.value)
              }
              placeholder="#008200 or rgb(153,26,0)"
              helperText="Optional: override theme palette with custom color (hex, rgb, rgba). Leave it empty to use default colors."
              type="text"
            />
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <DatePicker
                label="Publish Date"
                value={
                  formData.publishDate ? dayjs(formData.publishDate) : null
                }
                format="DD-MM-YYYY"
                onChange={handlePublishDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText:
                      "Optional: when the banner becomes active. If empty, it will be shown immediately.",
                  },
                }}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <DatePicker
                label="Unpublish Date"
                value={
                  formData.unpublishDate ? dayjs(formData.unpublishDate) : null
                }
                format="DD-MM-YYYY"
                onChange={handleUnpublishDateChange}
                minDate={publishDayjs || undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: unpublishInvalid
                      ? "Unpublish date must be after publish date"
                      : "Optional: when the banner should stop showing. If empty, it will be shown indefinitely.",
                    error: unpublishInvalid,
                  },
                }}
              />
            </FormControl>
          </LocalizationProvider>
        </Box>

        {/* Live preview in realistic context */}
        <Box sx={{ my: 2 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Preview of how the banner will appear:
              </Typography>
              <Divider sx={{ my: 2 }} />
              <AlertBanner
                id="preview-banner"
                text={formData.text}
                severity={mapCategoryToSeverity(formData.category)}
                variant={mapVariant(formData.variant)}
                isActive={true}
                customBackgroundColor={formData.customBackgroundColor}
                previewMode={true}
              />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || formInvalid}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
          <Button
            type="button"
            variant="outlined"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </div>
  );
};

export default BannerConfigForm;
